import { Badge } from "@/components/design-system/badge";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppDispatch } from "@store";
import {
  EllipsisVertical,
  Eye,
  SquarePen,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Using the GroupSchoolAdminModel from types
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import { downloadGroupSchoolAdminService } from "@/service/group-school-admin.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { DownloadGroupSchoolAdminQuery, GroupSchoolAdminModel } from "@/types/group-school-admin.model";
import {
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { deleteGroupSchoolAdmin, fetchGroupSchoolAdmins, selectGroupSchoolAdminActionLoading, selectGroupSchoolAdminLoading, selectGroupSchoolAdmins, selectNoGroupSchoolAdmins, selectTotalItems, updateGroupSchoolAdmin, } from "@/store/slices/groupSchoolAdminSlice";

// Extended interface for UI display purposes
interface GroupSchoolAdminDisplay extends GroupSchoolAdminModel {
  sno?: number;
  name: string;
  title: string;
  email: string;
  contact_number: string;
  no_of_schools: number;
  is_active: boolean;
}

// Removed static groupSchoolAdminData as we'll fetch from API

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function GroupSchoolAdminListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const dispatch = useDispatch<AppDispatch>();
  const groupSchoolAdmins = useSelector(selectGroupSchoolAdmins);
  const loading = useSelector(selectGroupSchoolAdminLoading);
  const actionLoading = useSelector(selectGroupSchoolAdminActionLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const noGroupSchoolAdmins = useSelector(selectNoGroupSchoolAdmins);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupSchoolAdminToDelete, setGroupSchoolAdminToDelete] = useState<string | null>(null);

  const [activateModalOpen, setActivateModalOpen] = useState(false);

  const [groupSchoolAdminToToggle, setGroupSchoolAdminToToggle] = useState<{
    id: string;
    is_active: boolean;
  } | null>(null);

  const navigate = useNavigate();

  // const router = useRouter();

  const columns: TableColumn<GroupSchoolAdminDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "name",
      title: "Admin Name",
      dataIndex: "name",
      minWidth: 222,
      maxWidth: 222,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "title",
      title: "Title",
      dataIndex: "title",
      minWidth: 198,
      maxWidth: 198,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.title}
        </div>
      ),
    },
    {
      key: "no_of_schools",
      title: "No. of Schools",
      dataIndex: "no_of_schools",
      minWidth: 200,
      maxWidth: 200,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.no_of_schools}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record?.email || "N/A"}
        </div>
      ),
    },
    {
      key: "contact",
      title: "Contact Number",
      minWidth: 200,
      maxWidth: 200,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record?.contact_number || "N/A"}
        </div>
      ),
    },
    {
      key: "status",
      title: "Active/Inactive",
      minWidth: 200,
      maxWidth: 200,
      align: "center",
      sortable: true,
      render: (_, record) => (
        <Badge variant={record.is_active ? "success" : "danger"}>
          {record.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_GROUP_SCHOOL_ADMIN,
      ROLE_PERMISSIONS.ACTION_EDIT_GROUP_SCHOOL_ADMIN,
      ROLE_PERMISSIONS.ACTION_DELETE_GROUP_SCHOOL_ADMIN,
      ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_GROUP_SCHOOL_ADMIN,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 180,
      maxWidth: 180,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_GROUP_SCHOOL_ADMIN,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() =>
                  navigate(`/group-school-admin/view/${record.id}`)
                }
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_GROUP_SCHOOL_ADMIN,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() =>
                  navigate(`/group-school-admin/edit/${record.id}`)
                }
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_GROUP_SCHOOL_ADMIN,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setGroupSchoolAdminToDelete(record.id);
                  setDeleteModalOpen(true);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasRolePermissions(userRole?.permissions as string[], [
            ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_GROUP_SCHOOL_ADMIN,
          ]) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                    <EllipsisVertical className="w-6 h-6 text-[#1C1B1F]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-[180px] !p-0 rounded-[8px] border border-[#EEE] bg-[#FFF] shadow-[0_30px_40px_0_rgba(0,0,0,0.15)]"
                >
                  {hasMenuPermission(
                    ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_GROUP_SCHOOL_ADMIN,
                    userRole?.permissions as string[]
                  ) && (
                      <DropdownMenuItem
                        className="h-[42px] px-4 py-0 flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED] [&_svg]:!size-6"
                        onClick={() => {
                          setGroupSchoolAdminToToggle({
                            id: record.id,
                            is_active: !record.is_active,
                          });
                          setActivateModalOpen(true);
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 w-full">
                          {record.is_active ? (
                            <>
                              <span>Deactivate</span>
                              <ToggleLeft className="w-6 h-6 text-[#FF152C]" />
                            </>
                          ) : (
                            <>
                              <span>Activate</span>
                              <ToggleRight className="w-6 h-6 text-[#00C41D]" />
                            </>
                          )}
                        </div>
                      </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
        </div>
      ),
    });
  }

  // Handle delete school
  const handleDeleteGroupSchoolAdmin = () => {
    if (groupSchoolAdminToDelete) {
      dispatch(deleteGroupSchoolAdmin(groupSchoolAdminToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setGroupSchoolAdminToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Group School Admin:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle activate/deactivate groupSchoolAdmin
  const handleToggleActivation = () => {
    if (groupSchoolAdminToToggle) {
      const { id, is_active } = groupSchoolAdminToToggle;
      dispatch(
        updateGroupSchoolAdmin({
          id,
          groupSchoolAdminData: { is_active },
        })
      )
        .unwrap()
        .then(() => {
          setActivateModalOpen(false);
          setGroupSchoolAdminToToggle(null);

          dispatch(
            fetchGroupSchoolAdmins({ page: currentPage, pageSize: ITEMS_PER_PAGE })
          );

          successToast("Group School Admin status updated successfully", "");
        })
        .catch((error) => {
          console.log("Failed to update group school admin status:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadGroupSchoolAdminQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
      };

      const response = await downloadGroupSchoolAdminService(type, queryParams);

      downloadFileFromBlob(response, type, "group_school_admin_report");

      successToast(
        "Export Successful",
        `Group School Admins exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting Group School Admins as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME",
      title: "TTL",
      email: "AEML",
      contact: "CNUM",
      status: "ACT",
      no_of_schools: "SCHCNT"
    };
    return sortFieldMap[key] || key;
  };

  // Fetch groupSchoolAdmins on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Create an AbortController to cancel duplicate requests
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
        sortBy: getSortField(sortConfig.key),
      }
      : {};

    // Use a timeout to debounce the API call
    const timeoutId = setTimeout(() => {
      // Only proceed if the request hasn't been aborted
      if (!signal.aborted) {
        dispatch(
          fetchGroupSchoolAdmins({
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            search: searchValue,
            fromDate: fromDate,
            toDate: toDate,
            ...sortParams,
          })
        );
      }
    }, 0); // 0ms delay still puts it in the next event loop cycle

    // Cleanup function to abort any pending requests when dependencies change
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig]);

  if (noGroupSchoolAdmins) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No Group School Admins Added yet
              </p>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Start by creating your first groupSchoolAdmins to students
              </p>

              {hasMenuPermission(
                ROLE_PERMISSIONS.BUTTON_ADD_GROUP_SCHOOL_ADMIN,
                userRole?.permissions as string[]
              ) && (
                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() => navigate("/group-school-admin/add")}
                  >
                    Add Admin
                  </Button>
                )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden  h-full flex flex-1 flex-col">
      <ListHeader
        title="Admin List"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {/* Export Button */}
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_GROUP_SCHOOL_ADMIN,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_GROUP_SCHOOL_ADMIN,
              userRole?.permissions as string[]
            ) && (
                <Button
                  variant="primary"
                  size="md"
                  className="w-[161px] h-[48px] bg-[#5C88C5] text-white"
                  onClick={() => navigate("/group-school-admin/add")}
                >
                  Add Admin
                </Button>
              )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          {/* {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-300px)] w-full">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading groupSchoolAdmins...</p>
              </div>
            </div>
          ) : ( */}
          <Table
            columns={columns}
            data={groupSchoolAdmins} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            // className="body-only-table"
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          {/* )} */}
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems} // Use actual total items from data
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Group School Admins"
      />

      {/* Delete Group School Admin Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Group School Admin"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Group School Admin ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteGroupSchoolAdmin()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>

      {/* Activate/Deactivate Group School Admin Confirmation Modal */}
      <Modal
        isOpen={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        title={groupSchoolAdminToToggle?.is_active ? "Activate" : "Deactivate"}
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            {groupSchoolAdminToToggle?.is_active ? (
              <>Are you sure you want to activate the Group School Admin?</>
            ) : (
              <>
                Are you sure you want to deactivate the Group School Admin?
                <br />
                Once deactivated, teacher and student credentials will no longer
                work.
              </>
            )}
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setActivateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleToggleActivation()}
            disabled={actionLoading}
          >
            {actionLoading
              ? "Processing..."
              : groupSchoolAdminToToggle?.is_active
                ? "Yes, Activate"
                : "Yes, Deactivate"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
